import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  acceptCollaborationCancellation,
  Api,
  ClientResponseDto,
  ClientsListResponseDto,
  EntityStatus,
  getClient,
  getClients,
  rejectCollaborationCancellation,
  terminateCollaboration,
} from '@taxora-api';
import {
  AcceptCancellationRequest,
  LoadCancellationRequests,
  LoadClientDetail,
  LoadClients,
  RejectCancellationRequest,
  ResetClientDetail,
  ResetClients,
  TerminateCollaboration,
} from './clients.actions';

export type CollaborationStatus = EntityStatus;

export type CollaborationDto = ClientResponseDto;

interface ClientsStateModel {
  result: ClientsListResponseDto | null;
  isLoading: boolean;
  hasError: boolean;
  clientDetail: CollaborationDto | null;
  isLoadingDetail: boolean;
  hasDetailError: boolean;
  cancellationRequests: CollaborationDto[];
  isCancellationRequestsLoading: boolean;
  isTerminating: boolean;
  isProcessingCancellation: boolean;
}

@State<ClientsStateModel>({
  name: 'clientsState',
  defaults: {
    result: null,
    isLoading: false,
    hasError: false,
    clientDetail: null,
    isLoadingDetail: false,
    hasDetailError: false,
    cancellationRequests: [],
    isCancellationRequestsLoading: false,
    isTerminating: false,
    isProcessingCancellation: false,
  },
})
@Injectable()
export class ClientsState {
  private readonly api = inject(Api);

  @Selector()
  static result(state: ClientsStateModel): ClientsListResponseDto | null {
    return state.result;
  }

  @Selector()
  static isLoading(state: ClientsStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static hasError(state: ClientsStateModel): boolean {
    return state.hasError;
  }

  @Selector()
  static clientDetail(state: ClientsStateModel): CollaborationDto | null {
    return state.clientDetail;
  }

  @Selector()
  static isLoadingDetail(state: ClientsStateModel): boolean {
    return state.isLoadingDetail;
  }

  @Selector()
  static hasDetailError(state: ClientsStateModel): boolean {
    return state.hasDetailError;
  }

  @Selector()
  static cancellationRequests(state: ClientsStateModel): CollaborationDto[] {
    return state.cancellationRequests;
  }

  @Selector()
  static isCancellationRequestsLoading(state: ClientsStateModel): boolean {
    return state.isCancellationRequestsLoading;
  }

  @Selector()
  static isTerminating(state: ClientsStateModel): boolean {
    return state.isTerminating;
  }

  @Selector()
  static isProcessingCancellation(state: ClientsStateModel): boolean {
    return state.isProcessingCancellation;
  }

  @Action(LoadClients)
  async load(
    { patchState }: StateContext<ClientsStateModel>,
    { params }: LoadClients,
  ): Promise<void> {
    patchState({ isLoading: true, hasError: false });
    try {
      const result = await this.api.invoke(getClients, params);
      patchState({ result });
    } catch (error) {
      console.error('Failed to load clients', error);
      patchState({ hasError: true });
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(ResetClients)
  reset({ patchState }: StateContext<ClientsStateModel>): void {
    patchState({ result: null, isLoading: false, hasError: false });
  }

  @Action(LoadClientDetail)
  async loadDetail(
    { patchState }: StateContext<ClientsStateModel>,
    { id }: LoadClientDetail,
  ): Promise<void> {
    patchState({ isLoadingDetail: true, hasDetailError: false });
    try {
      const clientDetail = await this.api.invoke(getClient, { id });
      patchState({ clientDetail });
    } catch (error) {
      console.error('Failed to load client detail', error);
      patchState({ hasDetailError: true });
    } finally {
      patchState({ isLoadingDetail: false });
    }
  }

  @Action(ResetClientDetail)
  resetDetail({ patchState }: StateContext<ClientsStateModel>): void {
    patchState({ clientDetail: null, isLoadingDetail: false, hasDetailError: false });
  }

  @Action(LoadCancellationRequests)
  async loadCancellationRequests(
    { patchState }: StateContext<ClientsStateModel>,
    { organizationId }: LoadCancellationRequests,
  ): Promise<void> {
    patchState({ isCancellationRequestsLoading: true });
    try {
      const result = await this.api.invoke(getClients, {
        organizationId,
        status: 'CANCELLATION_REQUESTED' as EntityStatus,
      });
      patchState({ cancellationRequests: result.data ?? [] });
    } catch (error) {
      console.error('Failed to load cancellation requests', error);
    } finally {
      patchState({ isCancellationRequestsLoading: false });
    }
  }

  @Action(TerminateCollaboration)
  async terminate(
    { patchState }: StateContext<ClientsStateModel>,
    { id }: TerminateCollaboration,
  ): Promise<void> {
    patchState({ isTerminating: true });
    try {
      const updated = await this.api.invoke(terminateCollaboration, { id });
      patchState({ clientDetail: updated });
    } catch (error) {
      console.error('Failed to terminate collaboration', error);
      throw error;
    } finally {
      patchState({ isTerminating: false });
    }
  }

  @Action(AcceptCancellationRequest)
  async acceptCancellation(
    { patchState, getState }: StateContext<ClientsStateModel>,
    { id }: AcceptCancellationRequest,
  ): Promise<void> {
    patchState({ isProcessingCancellation: true });
    try {
      const updated = await this.api.invoke(acceptCollaborationCancellation, { id });
      const remaining = getState().cancellationRequests.filter((r) => r.id !== id);
      const currentDetail = getState().clientDetail;
      patchState({
        cancellationRequests: remaining,
        clientDetail: currentDetail?.id === id ? updated : currentDetail,
      });
    } catch (error) {
      console.error('Failed to accept cancellation request', error);
      throw error;
    } finally {
      patchState({ isProcessingCancellation: false });
    }
  }

  @Action(RejectCancellationRequest)
  async rejectCancellation(
    { patchState, getState }: StateContext<ClientsStateModel>,
    { id, message }: RejectCancellationRequest,
  ): Promise<void> {
    patchState({ isProcessingCancellation: true });
    try {
      const updated = await this.api.invoke(rejectCollaborationCancellation, {
        id,
        body: { message },
      });
      const remaining = getState().cancellationRequests.filter((r) => r.id !== id);
      const currentDetail = getState().clientDetail;
      patchState({
        cancellationRequests: remaining,
        clientDetail: currentDetail?.id === id ? updated : currentDetail,
      });
    } catch (error) {
      console.error('Failed to reject cancellation request', error);
      throw error;
    } finally {
      patchState({ isProcessingCancellation: false });
    }
  }
}
